import moment from "../moment/moment";

import * as constants from "../constants";
import { getActualShifts, isPeriodIncludedInShift } from "./shifts";
import { sortByIdCallback } from "./utils";
import formatter from "./formatter";
import { floorToInterval } from "./math";
import { formatDuration } from "./time";


export const CONFIRMED_RESERVATION = "CONFIRMED_RESERVATION";
export const PENDING_RESERVATION = "PENDING_RESERVATION";
export const CANCELLED_RESERVATION = "CANCELLED_RESERVATION";

export const filterReservationsByName = (value, reservations) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const foundReservations = inputLength < 2 ? [] : reservations.filter(reservation => (
        reservation.last_name?.toLowerCase().slice(0, inputLength) === inputValue ||
        reservation.first_name?.toLowerCase().slice(0, inputLength) === inputValue));
    return enrichReservations(foundReservations);
};

export const sortReservationsByStartTime = reservations => [...reservations].sort((a, b) => moment(a.start_time) - moment(b.start_time));

export const filterOutOldReservations = (reservations = []) => reservations.filter(reservation => !reservation.hidden && moment(reservation.start_time) >= moment().add(-2, "hour"));

export const getTimeline = (startTimeValue) => {
    const startTime = moment(startTimeValue).add(-3, 'hour').unix() * 1000;
    const endTime = moment(startTimeValue).add(3, 'hour').unix() * 1000;
    return { startTime: startTime, endTime: endTime };
};

export const getReservationFullName = reservation => {
    return `${reservation.last_name ?? ''} ${reservation.first_name ?? ''}`.trim() || formatter.translate('booker:default_client_full_name');
}

export function getReservationGuests({ adults, children }) {
    return adults + children;
}

export const enrichReservation = reservation => {

    const guests = reservation.adults + reservation.children;

    const adultLabel = formatter.translate('booker:adult', { count: reservation.adults });
    const childLabel = formatter.translate('booker:child', { count: reservation.children });

    const guests_name = reservation.children > 0
        ? formatter.translate('booker:guest_summary.with_children', {
            count: guests,
            // adultCount: reservation.adults,
            adultLabel,
            // childCount: reservation.children,
            childLabel,
        })
        : formatter.translate('booker:guest_summary.without_children', {
            count: guests,
            // adultCount: reservation.adults,
            adultLabel,
        });

    const reservationFullName = getReservationFullName(reservation);
    return (
        {
            ...reservation,
            reservationStatus: reservation.cancelled ? CANCELLED_RESERVATION : reservation.confirmed ? CONFIRMED_RESERVATION : PENDING_RESERVATION,
            reservationLogo: reservation.cancelled ? "delete calendar" : reservation.confirmed ? "check" : "clock",
            reservationColor: reservation.cancelled ? "red" : reservation.confirmed ? "green" : "orange",
            reservationFullName,
            reservationGuestsDetails: guests_name,
            reservationNbGuests: guests,
            reservationStartTime: formatter.translate('common:full_datetime_format', {
                date: moment(reservation.start_time).format("dddd LL"),
                time: moment(reservation.start_time).format("LT"),
            }),
            start_time_date: moment(reservation.start_time).format("DD/MM/YYYY"),
            start_time_time: moment(reservation.start_time).format("HH:mm")
        }
    )
};

export const isReservationOverlaping = (reservationA, reservationB) => {

    const tables = [...reservationA.tables, ...reservationB.tables];
    const tableSet = new Set(tables);

    if (tableSet.size === tables.length) {
        return false;
    }

    const start_time = moment(reservationB.start_time);
    const duration = moment.duration(reservationB.duration);
    const end_time = moment(start_time).add(duration);

    const current_start_time = moment(reservationA.start_time);
    const current_duration = moment.duration(reservationA.duration);
    const current_end_time = moment(current_start_time).add(current_duration);

    return current_start_time.isBetween(start_time, end_time, undefined, "[)") || start_time.isBetween(current_start_time, current_end_time, undefined, "[)");
}

export const isReservationOverlapingOnList = (currentReservation, reservations = []) => {
    for (const reservation of reservations) {
        if (reservation.id == currentReservation.id)
            continue;
        const isOverlaping = isReservationOverlaping(currentReservation, reservation);
        if (isOverlaping) return true;
    }
    return false;
};

export const enrichReservations = (reservations) => reservations?.map(reservation => enrichReservation(reservation)) ?? [];

export const enrichReservationWithTables = (reservation, tables, t) => {
    if (reservation.tables) {
        const table_names = tables.length > 0 ?
            reservation.tables.map(tableId => tables.find(table => table.id === tableId)?.name) :
            [];
        // const table_text = reservation.tables.length > 1 ? constants.TABLES_TEXT : constants.TABLE_TEXT;
        const table_text = t('common:table', { count: reservation.tables.length });
        return {
            ...reservation,
            reservationTables: reservation.tables.length > 0 ? table_text + ": " + table_names.join(',') : "",
            reservationTablesNumber: table_names
        }
    }
};

export const enrichReservationsWithTables = (reservations, tables, t) => reservations.map(reservation => enrichReservationWithTables(reservation, tables, t));

export const getEventName = event => constants.EVENT_TYPES.find(eventType => eventType.value === event)?.text;

/**
 * 
 * @param {number[]} ids 
 * @param {any[]} options 
 * @param {any[]} categories 
 * @returns {any[]} 
 */
export const getSelectedSpecialOptions = (ids, options, categories) => {

    if (ids.length === 0)
        return [];

    const filteredOptions = options
        .filter(option => ids.includes(option.id));
    return categories
        .map(category => ({
            ...category,
            options: filteredOptions.filter(option => option.category === category.id),
        }))
        .filter(category => category.options.length > 0)
        .sort(sortByIdCallback);
};

export const setTimeFromString = (momentObj = moment(), time = "00:00:00") => {
    const [hours, minutes, seconds] = time.split(':').map(el => parseInt(el, 10));
    return moment(momentObj).hours(hours).minutes(minutes).seconds(seconds);
}

export const getNbReservations = reservations => reservations.length;

export function getTablesSeatsSum(tables) {
    return tables?.reduce((totalSeats, table) => totalSeats + table.seats, 0) ?? 0;
}

export function filterActiveTables(tables) {
    return tables?.filter(table => table.active && table.zone.active);
}

export function getReservationsGuestsSum(reservations) {
    return reservations?.reduce((totalGuests, reservation) =>
        totalGuests + reservation.adults + reservation.children
        , 0) ?? 0;
}

export function getReservationsOnTables(reservations, tables) {
    const tablesIdsSet = new Set(tables.map(table => table.id));
    const reservationsOnTables = reservations
        .filter(reservation => reservation.tables
            .some(tableId => tablesIdsSet.has(tableId))
        );
    return reservationsOnTables;
}

export const getNbGuests = getReservationsGuestsSum;

export const filterConfirmedReservations = reservations => {
    return reservations.filter(reservation => reservation.confirmed === true && reservation.cancelled === false);
};

export const filterPendingReservations = reservations => {
    return reservations.filter(reservation => reservation.confirmed === false && reservation.cancelled === false);
};

export const filterCancelledReservations = reservations => {
    return reservations.filter(reservation => reservation.cancelled === true);
};

export const countConfirmedReservations = reservations => {
    return filterConfirmedReservations(reservations).length;
};

export const countNumberPendingReservations = reservations => {
    return filterPendingReservations(reservations).length;
};

export const countNumberCancelledReservations = reservations => {
    return filterCancelledReservations(reservations).length;
};

export const filterReservationsByDay = (reservations, selectedDay) => {
    return reservations.filter(reservation => moment(reservation.start_time).isSame(selectedDay, 'day'));
};

export const groupReservationsByShifts = (
    reservations = [],
    shifts = [],
    selectedDay = moment(),
    status = null,
    showOld = true
) => {
    const effectiveShifts = getActualShifts(selectedDay, shifts);
    const groups = new Map();

    effectiveShifts.forEach(shift => {
        const from = setTimeFromString(selectedDay, shift.from_hour)
            .format(constants.DISPLAY_TIME_FORMAT);
        const to = setTimeFromString(selectedDay, shift.to_hour)
            .format(constants.DISPLAY_TIME_FORMAT);
        const key = `${from} - ${to}`;
        groups.set(key, {
            shift,
            sortTime: setTimeFromString(selectedDay, shift.from_hour),
            reservations: [],
        });
    });

    const filteredReservations = (showOld ? reservations : filterOutOldReservations(reservations))
        .filter(reservation => (
            (status === constants.PENDING_MENU && (!reservation.cancelled && !reservation.confirmed)) ||
            (status === constants.CONFIRMED_MENU && (!reservation.cancelled && reservation.confirmed)) ||
            (status === constants.CANCELLED_MENU && reservation.cancelled) ||
            (!status)
        ));

    filteredReservations.forEach(reservation => {
        const startTime = moment(reservation.start_time);
        if (!moment(startTime).startOf('day').isSame(moment(selectedDay).startOf('day'))) {
            return;
        }
        const foundShift = effectiveShifts.find(shift => {
            const from = setTimeFromString(selectedDay, shift.from_hour);
            const to = setTimeFromString(selectedDay, shift.to_hour);
            return startTime.isBetween(from, to, undefined, "[)");
        }) ?? null;

        let key = startTime.format(constants.DISPLAY_TIME_FORMAT);
        if (foundShift) {
            const from = setTimeFromString(selectedDay, foundShift.from_hour)
                .format(constants.DISPLAY_TIME_FORMAT);
            const to = setTimeFromString(selectedDay, foundShift.to_hour)
                .format(constants.DISPLAY_TIME_FORMAT);
            key = `${from} - ${to}`;
        }

        if (!groups.has(key)) {
            groups.set(key, {
                shift: foundShift,
                sortTime: startTime.clone(),
                reservations: [],
            });
        }

        const group = groups.get(key);
        groups.set(key, {
            ...group,
            reservations: [
                ...group.reservations,
                reservation,
            ],
        });
    });
    // Sort the final groups by actual start time, rather than insertion order
    const sortedEntries = [...groups.entries()].sort(
        ([, a], [, b]) => a.sortTime.valueOf() - b.sortTime.valueOf()
    );

    return new Map(
        sortedEntries.map(([key, { shift, reservations }]) => [key, { shift, reservations }])
    );
};

export const splitReservationsByStartTime = (reservations, shifts) => {
    if (reservations.length === 0) return [];

    const reservationMoment = moment(reservations[0].start_time);

    const reservationsDate = reservationMoment.format("YYYY-MM-DD");
    const reservationsDay = reservationMoment.isoWeekday() - 1;
    const dayShifts = getActualShifts(reservationMoment, shifts);
    const augmentedDayShifts = dayShifts.map(dayShift => {
        return {
            ...dayShift,
            shift_start_time: moment(reservationsDate + " " + dayShift.from_hour, "YYYY-MM-DD HH:mm:ss"),
            shift_end_time: moment(reservationsDate + " " + dayShift.to_hour, "YYYY-MM-DD HH:mm:ss")
        }
    })

    let current_group = null;
    let current_array = [];
    let group_array = [];

    reservations.forEach(reservation => {
        let el_group = null;
        const reservationStartTime = moment(reservation.start_time);

        augmentedDayShifts.forEach(augmentedShift => {
            if (reservationStartTime >= augmentedShift.shift_start_time && reservationStartTime < augmentedShift.shift_end_time) {
                el_group = augmentedShift.shift_start_time.format("HH:mm") + " - " + augmentedShift.shift_end_time.format("HH:mm")
            }
        })

        if (!el_group) {
            el_group = reservationStartTime.format("HH:mm");
        }

        const augmentedReservation = {
            ...reservation,
            reservationGroup: el_group
        }

        if (current_group === null) {
            current_array.push(augmentedReservation);
            current_group = el_group;
        }
        else {
            if (el_group === current_group) {
                current_array.push(augmentedReservation)
            }
            else {
                group_array.push(current_array);
                current_array = [augmentedReservation];
                current_group = el_group;
            }
        }
    });
    group_array.push(current_array);
    return group_array;
};

// export const splitReservationsByStartTime = reservations => {
//     if (reservations.length === 0) return [];
//
//     let current_group = null;
//     let current_array = [];
//     let group_array = [];
//
//     reservations.forEach( reservation => {
//         const el_group = moment(reservation.start_time).format("HH:mm");
//         if (current_group === null) {
//             current_array.push(reservation);
//             current_group = el_group;
//         }
//         else {
//             if (el_group === current_group) {
//                 current_array.push(reservation)
//             }
//             else {
//                 group_array.push(current_array);
//                 current_array = [reservation];
//                 current_group = el_group;
//             }
//         }
//     });
//     group_array.push(current_array);
//
//     return group_array;
// };

export const getCalendarSummary = (reservations = []) => {
    return reservations
        .filter(reservation => !reservation.cancelled)
        .reduce((dict, reservation) => {
            const startTime = moment(reservation.start_time);
            const dateKey = startTime.format(constants.DATE_FORMAT);
            const dateValue = dict[dateKey] ?? { total_seats: 0, reservations_count: 0 };
            const partySize = reservation.adults + reservation.children;
            dict[dateKey] = { total_seats: dateValue.total_seats + partySize, reservations_count: dateValue.reservations_count + 1 };
            return dict;
        }, {});
};

export const getTimelineItems = reservations => {
    const processedReservation = [];
    const colors = constants.TIMELINE_ITEMS_COLORS;

    let colorIdx = 0;

    const getClassName = (reservation) => {
        if (reservation.should_be_called && !reservation.has_been_called) {
            return "should_be_called";
        }
        else if (reservation.confirmed) {
            if (reservation.arrived) {
                return "arrived";
            }
            else if (reservation.no_show) {
                return "no_show";
            }
            else {
                return "confirmed";
            }
        }
        else {
            return "pending";
        }
    }

    reservations.map(
        reservation => {
            const durationMoment = moment.duration(reservation.duration);
            const itemStartTime = moment(reservation.start_time);
            const itemEndTime = moment(reservation.start_time).add(durationMoment);
            const className = getClassName(reservation);

            if (!reservation.cancelled) {
                if (reservation.tables.length > 1) {
                    reservation.tables.map(
                        table => {
                            processedReservation.push({
                                ...reservation,
                                timelineGroupId: table,
                                timelineGroupName: table,
                                timelineItemId: reservation.id + '-' + String(table),
                                timelineItemStartTime: itemStartTime,
                                timelineItemEndTime: itemEndTime,
                                className: className,
                                timelineColor: colors[colorIdx]
                            });
                        }
                    );
                    colorIdx += 1;
                    colorIdx = colorIdx % colors.length;
                } else {
                    processedReservation.push({
                        ...reservation,
                        timelineGroupId: reservation.tables[0],
                        timelineGroupName: reservation.tables[0],
                        timelineItemId: reservation.id.toString(),
                        timelineItemStartTime: itemStartTime,
                        timelineItemEndTime: itemEndTime,
                        className: className
                    });
                }
            }
        }
    );
    return processedReservation;
};

export const getEmptyCurrentReservation = () => {
    return {
        id: null,
        customer_id: null,
        first_name: "",
        last_name: "",
        start_time: moment().startOf("hour").add(1, "hour"),
        duration: null,
        adults: 0,
        children: 0,
        event: constants.EVENT_TYPES[0].value,
        custom_event: null,
        cake: false,
        stroller: false,
        highchair: false,
        reduced_mobility: false,
        terrace: false,
        comment: "",
        tables: [],
        confirmed: true,
        confirmed_at: null,
        created_at: null,
        updated_at: null,
        customer: null,
        token: null,
        cancelled: false,
        cancelled_by: null,
        cancelled_at: null,
        no_show: false,
        no_show_at: null,
        read: false,
        hidden: false,
        restaurant_cancel_message: ""
    };
};

export const getReservationStatus = reservation => {
    const { cancelled, confirmed, hidden, } = reservation;

    if (hidden)
        return constants.RESERVATION_STATUS.ARCHIVED;

    else if (cancelled)
        return constants.RESERVATION_STATUS.CANCELLED;

    else if (confirmed)
        return constants.RESERVATION_STATUS.CONFIRMED;

    return constants.RESERVATION_STATUS.PENDING;
};

export const emptyTablesForPending = (reservation) => {
    return {
        ...reservation,
        tables: reservation.confirmed ? reservation.tables : [],
    };
}

export function getReservationEndTime(reservation) {
    const reservationDuration = moment.duration(reservation.duration);
    const reservationEndTime = moment(reservation.start_time).add(reservationDuration);
    return reservationEndTime;
};

export function isReservationIncludedInShift(reservation, shift) {
    const reservationStartTime = moment(reservation.start_time);
    const reservationEndTime = getReservationEndTime(reservation);
    return isPeriodIncludedInShift(
        reservationStartTime,
        reservationEndTime,
        shift,
    );
}

/**
 * Checks whether a given moment falls within a reservation's time range.
 *
 * The range is inclusive of the start time and exclusive of the end time: [start, end).
 *
 * @param {Object} reservation - The reservation to check against.
 * @param {string} reservation.start_time - ISO 8601 start time of the reservation.
 * @param {moment.Moment} givenMoment - The moment to evaluate.
 * @returns {boolean} `true` if `givenMoment` is within the reservation's range, `false` otherwise.
 *
 * @example
 * const isIncluded = isMomentIncludedInReservation(reservation, moment());
 * if (isIncluded) {
 *   console.log("The reservation is currently active.");
 * }
 */
export function isMomentIncludedInReservation(reservation, givenMoment) {
    const reservationStartTime = moment(reservation.start_time);
    const reservationEndTime = getReservationEndTime(reservation);

    return givenMoment.isBetween(
        reservationStartTime,
        reservationEndTime,
        undefined,
        "[)",
    );
}

/**
 * Checks if a given moment occurs after a specified duration within a reservation.
 *
 * @param {Object} reservation - The reservation object.
 * @param {string|Date} reservation.start_time - The start time of the reservation.
 * @param {moment.Moment} givenMoment - The moment to evaluate.
 * @param {number} givenTimeSec - The minimum elapsed time in seconds from the reservation start.
 * @returns {boolean} `true` if the moment is within the reservation AND at least `givenTimeSec`
 *                    seconds have elapsed since the reservation started, `false` otherwise.
 */
export function isMomentAfterSomeTimeInReservation(reservation, givenMoment, givenTimeSec) {
    const reservationStartTime = moment(reservation.start_time);
    const isMomentInReservation = isMomentIncludedInReservation(reservation, givenMoment);

    return (
        isMomentInReservation
        && (
            givenMoment.diff(reservationStartTime, "seconds")
            >= givenTimeSec
        )
    );
}

/**
 * Checks whether a given moment occurs after a reservation's start time.
 *
 * @param {Object} reservation - The reservation to check against.
 * @param {string} reservation.start_time - ISO 8601 start time of the reservation.
 * @param {moment.Moment} givenMoment - The moment to evaluate.
 * @returns {boolean} `true` if `givenMoment` is strictly after the reservation's start time, `false` otherwise.
 *
 * @example
 * const hasStarted = isMomentAfterReservationStartTime(reservation, moment());
 */
export function isMomentSameOrAfterReservationStartTime(reservation, givenMoment) {
    const reservationStartTime = moment(reservation.start_time);
    return givenMoment.isSameOrAfter(reservationStartTime);
}

/**
 * Adjusts a reservation's duration when a given moment falls within its time range.
 *
 * If `endMoment` occurs during the reservation, the duration is recalculated
 * from the reservation's start time to `endMoment`, rounded down to the nearest
 * `floorInterval` seconds. The updated duration is applied both to `currentRes`
 * (side effect) and returned as a new reservation object.
 *
 * Returns unchanged `reservation` if `endMoment` is outside the reservation's time range.
 *
 * @param {Object} reservation - The reservation object to evaluate.
 * @param {string} reservation.start_time - ISO 8601 start time of the reservation.
 * @param {moment.Moment} endMoment - The moment to compare against the reservation's range.
 * @param {number} floorInterval - Interval in seconds to which the new duration is rounded down.
 * @returns {{ duration: string } & Object} A new reservation object with the
 *     updated formatted duration, or unchanged `reservation` if `endMoment` is not during the reservation.
 */
export function endReservationAtMoment(reservation, endMoment, floorInterval) {

    const isEndMomentDuringReservation = isMomentIncludedInReservation(reservation, endMoment);

    if (!isEndMomentDuringReservation)
        return reservation;

    const reservationStartTime = moment(reservation.start_time);
    const reservationStartTimeDeltaFromNow = endMoment.diff(reservationStartTime, "seconds");
    const newReservationDurationSeconds = floorToInterval(
        reservationStartTimeDeltaFromNow,
        floorInterval,
    )
    const newReservationDuration = moment.duration(newReservationDurationSeconds, "seconds");
    const formattedNewReservationDuration = formatDuration(newReservationDuration);

    return {
        ...reservation,
        duration: formattedNewReservationDuration,
    }
}


// WEBPACK FOOTER //
// ./src/helpers/reservations.js